import React from 'react';
import { formatAttendeeNameFromResource } from '../../../utils/formatAttendeeName';
import moment from 'moment';
import { filterRoomRatesByRegType } from '../../../utils/transformHotelData';
import { getIn } from 'icepick';
import { DATE_CHANGE_ERROR_INVALID, DATE_CHANGE_EMPTY_DATE } from 'nucleus-core/forms/PickADateBase';
import { AVAILABLE_ROOMMATES_OPTION, REG_TYPE_MATCHING_OPTION, ROOMMATE_PREFERENCE_OPTION, ROOMMATE_PREFERENCE_TYPE }
  from '../../../utils/travelConstants';

export const ATTENDEE_TYPE_INVITEE_AND_GUEST = 'inviteeAndGuest';

/**
 * Should allow modification (edit/delete) based on site editor settings
 * @param {*} display
 * @param {*} isPlanner
 */
export function shouldAllowModification(display, isPlanner) {
  return isPlanner || (display.registrantsModifyRequest && (!display.allowModificationUntil ||
    new Date().getTime() < new Date(display.allowModificationUntil).getTime()));
}

/**
 * Should show edit selection based on different criterias
 * @param {*} display
 * @param {*} isPlanner
 * @param {*} roomRequest
 */
export function shouldShowEditSelection(display, isPlanner, roomRequest) {
  const isNewRequest = !roomRequest.hotelReservationDetailId;
  // always show in case planner, new request or when roommate is not matched on the basis of site ediitor settings
  return isNewRequest || ((isPlanner || !roomRequest.isRoommateMatched) && // ignore roommate match in case of planner
    shouldAllowModification(display, isPlanner));
}

export function getRegPathIdOfRequestedRoomAttendee(groupedRoom, registrations) {
  const registrationIds = groupedRoom.map(a => a.attendeeRegistrationId);
  if (registrationIds.filter(a => a === registrations.primary.eventRegistrationId).length > 0) {
    return registrations.primary.registrationPathId;
  }
  return getIn(registrations.guests.find(g => registrationIds.filter(a => a === g.eventRegistrationId).length > 0),
    ['registrationPathId']);
}

export function makeAttendeeOptions({ primary = { attendee: { personalInformation: {} } }, guests = [] } = {},
  showGuests = true, translate) {
  if (!showGuests || guests.length === 0) {
    return [];
  }
  return [{
    category: translate('EventWidgets_HotelRequest_Labels_Primary__resx'),
    items: [{
      name: getPrimaryAttendeeDisplayName(primary.attendee.personalInformation, translate),
      value: primary.eventRegistrationId
    }]
  }, {
    category: translate('EventWidgets_HotelRequest_Labels_Guests__resx'),
    items: [...guests.map(guest => (
      {
        name: formatAttendeeNameFromResource(guest.attendee.personalInformation, translate),
        value: guest.eventRegistrationId
      }))]
  }];
}

export function makeAttendeeOptionsForSiteEditor({ primary: _primary = { attendee: { personalInformation: {} } },
  guests: _guests = [] } = {}, showGuests = true, translate) {
  if (!showGuests) {
    return [];
  }
  return [{
    category: translate('EventWidgets_HotelRequest_Labels_Primary__resx'),
    items: [{
      name: translate('EventWidgets_HotelRequest_PrimaryNoName__resx'),
      value: 'primary'
    }]
  }];
}

export function getAdvancedRule(advancedRules = [], admissionItemId, registrationTypeId) {
  return advancedRules.find(rule => rule.isActive && rule.admissionItem.includes(admissionItemId)
      && rule.contactType.includes(registrationTypeId) && rule.arrivalDate && rule.departureDate);
}

/**
 * gets the effective check in and checkout date from advance rule
 * @param {*} advanceRule
 * @param {*} isPlanner
 */
export function getDatesFromAdvancedRule(advanceRule, isPlanner) {
  let checkinDate;
  let checkoutDate;
  let arrivalDate = new Date(advanceRule.arrivalDate);
  let departureDate = new Date(advanceRule.departureDate);

  if (advanceRule.shoulderDateSetup.isAllowed && (isPlanner || advanceRule.shoulderDateSetup.isAllowedByInvitee)) {
    checkinDate = moment(arrivalDate).subtract(advanceRule.shoulderDateSetup.arrivalShoulderDays, 'day');
    checkoutDate = moment(departureDate).add(advanceRule.shoulderDateSetup.departureShoulderDays, 'day');
  } else {
    checkinDate = moment(arrivalDate);
    checkoutDate = moment(departureDate);
  }
  return { checkinDate, checkoutDate, arrivalDate, departureDate };
}

export const getAllowedDateRange = (hotel, isPlanner, checkinDate, checkoutDate, hotelRegRule) => {
  const { arrivalDate: earliestCheckInDate, departureDate: latestCheckOutDate, hasShoulderDays, checkInShoulderDays,
    checkOutShoulderDays, allowRequestShoulderDays } = hotel;

  let earliestAllowedCheckInDate = hasShoulderDays && (isPlanner || allowRequestShoulderDays)
    ? moment(earliestCheckInDate).subtract(checkInShoulderDays, 'day')
    : moment(earliestCheckInDate);
  let latestAllowedCheckOutDate = hasShoulderDays && (isPlanner || allowRequestShoulderDays)
    ? moment(latestCheckOutDate).add(checkOutShoulderDays, 'day')
    : moment(latestCheckOutDate);

  if (hotelRegRule) {
    const hotelRegRuleDate = getDatesFromAdvancedRule(hotelRegRule, isPlanner);
    // choose the smallest range date from reg rule and hotel dates
    earliestAllowedCheckInDate = earliestAllowedCheckInDate.isBefore(hotelRegRuleDate.checkinDate) ?
      hotelRegRuleDate.checkinDate : earliestAllowedCheckInDate;
    latestAllowedCheckOutDate = latestAllowedCheckOutDate.isAfter(hotelRegRuleDate.checkoutDate) ?
      hotelRegRuleDate.checkoutDate : latestAllowedCheckOutDate;
  }

  return { earliestAllowedCheckInDate, latestAllowedCheckOutDate };
};

export const hasRequestedOnlyShoulderDatesRequiringApproval = (hotel, isPlanner, checkinDate, checkoutDate,
  hotelRegRule) => {
  const { arrivalDate: earliestCheckInDate, departureDate: latestCheckOutDate, allowRequestShoulderDays,
    hasPlannerApprovalShoulderDays } = hotel;
  let areDatesOnlyShoulderDatesRequiringApproval = allowRequestShoulderDays && hasPlannerApprovalShoulderDays &&
    (moment(checkoutDate).isSameOrBefore(earliestCheckInDate)
      || moment(checkinDate).isSameOrAfter(latestCheckOutDate));

  if (hotelRegRule) {
    const hotelRegRuleDate = getDatesFromAdvancedRule(hotelRegRule, isPlanner);
    areDatesOnlyShoulderDatesRequiringApproval = hotelRegRule.shoulderDateSetup.isAllowedByInvitee &&
      hotelRegRule.shoulderDateSetup.isPlannerApprovalRequired &&
      (moment(checkoutDate).isSameOrBefore(moment(hotelRegRuleDate.arrivalDate))
        || moment(checkinDate).isSameOrAfter(moment(hotelRegRuleDate.departureDate)));
  }

  return areDatesOnlyShoulderDatesRequiringApproval;
};

/**
 * Filter the hotels based on the reg path id and other conditions
 * @param {*} hotels
 * @param {*} registrationPathId
 */
export function filterHotels(hotels, registrationPathId, isPlanner) {
  return hotels.filter(hotel => (hotel.isActive || isPlanner) && hotel.roomTypes
                && hotel.roomTypes.filter(room => (room.isOpenForRegistration || isPlanner)
                && room.associatedRegPathSettings
                && room.associatedRegPathSettings[registrationPathId]).length > 0);
}

/**
 * Get the number of rooms text that is shown on the hotel request summary view, for the given params
 */
export const getNumberOfRoomsTextRequestSummary =
({ isSingleRequest, sequence, firstName, lastName, translate, resolve }) => {
  const hasFirstNameOrLastName = firstName || lastName;
  return (
    <div {...resolve('roomRequestFor')}>
      <div {...resolve('roomNumber')}>
        { isSingleRequest
          ? translate('EventWidgets_RegSummary_RoomRequested__resx')
          : translate('EventWidgets_HotelRequest_RoomWithNumber__resx', { number: sequence })
        }
      </div>
      { hasFirstNameOrLastName &&
        <div>&nbsp;{ translate('EventWidgets_HotelRequestSummary_RequestedFor__resx', { firstName, lastName }) }</div>
      }
      { isSingleRequest && !hasFirstNameOrLastName &&
        <div>&nbsp;{ translate('EventWidgets_HotelRequest_RoomRequested__resx') }</div>
      }
    </div>
  );
};

/**
 * get default check in and checkout dates to be populated on hotel request widget
 * @param {*} props
 */
export function getDefaultCheckinCheckoutDates({ hotels, hotelRegRules, admissionItemId, registrationTypeId,
  eventStartDate, eventEndDate, registrationPathId, isPlanner }) {
  const advanceRule = getAdvancedRule(hotelRegRules, admissionItemId, registrationTypeId);
  let checkinDate = moment(eventStartDate)
    .subtract(1, 'day').startOf('day').toDate();
  let checkoutDate = new Date(eventEndDate);
  if (advanceRule && advanceRule.defaultArrivalDate && advanceRule.defaultDepartureDate) {
    checkinDate = new Date(advanceRule.defaultArrivalDate);
    checkoutDate = new Date(advanceRule.defaultDepartureDate);
  } else {
    // filter hotels based on active and reg type room association
    const filteredHotels = filterHotels(hotels, registrationPathId, isPlanner);
    if (filteredHotels && filteredHotels.length) {
      checkinDate = filteredHotels.sort((a, b) => new Date(a.arrivalDate).getTime() - new Date(b.arrivalDate)
        .getTime())[0].arrivalDate;
      checkinDate = moment(checkinDate).startOf('day').toDate();
      checkoutDate = filteredHotels.sort((a, b) => new Date(b.departureDate).getTime() - new Date(a.departureDate)
        .getTime())[0].departureDate;
      checkoutDate = moment(checkoutDate).startOf('day').toDate();
    }
  }
  return { checkinDate, checkoutDate };
}

/**
 * Gets the warning message for guest and invitee based on passed checkInOut dates.
 * @param hotel
 * @param hotelRegRules
 * @param checkinDate
 * @param checkoutDate
 * @param isPlanner
 * @param attendee
 * @returns {{errorMessage: string, hasInvalidRequestedDates: boolean, hasRequestedOnlyShoulderDates: boolean}}
 */
export function getValidationMessageForAttendee({ hotel, hotelRegRules, checkinDate, checkoutDate, isPlanner },
  attendee) {
  if (!attendee) {
    return;
  }
  const admissionItem = attendee.productRegistrations.find(p => p.productType === 'AdmissionItem' &&
    p.requestedAction === 'REGISTER');

  let regRuleApplied = attendee.registrationTypeId && admissionItem &&
    getAdvancedRule(hotelRegRules, admissionItem.productId, attendee.registrationTypeId);

  const { earliestAllowedCheckInDate, latestAllowedCheckOutDate } =
    getAllowedDateRange(hotel, isPlanner, checkinDate, checkoutDate, regRuleApplied);

  const areDatesOnlyShoulderDatesRequiringApproval = hasRequestedOnlyShoulderDatesRequiringApproval(hotel, isPlanner,
    checkinDate, checkoutDate, regRuleApplied);

  const isHotelUnAvailable = !(moment(checkinDate).isSameOrAfter(earliestAllowedCheckInDate.startOf('day'))
    && moment(checkoutDate).isSameOrBefore(latestAllowedCheckOutDate.startOf('day')));

  if (isHotelUnAvailable) {
    if (regRuleApplied) {
      return {
        errorMessage: regRuleApplied.warningMessage,
        hasRequestedOnlyShoulderDates: false,
        hasInvalidRequestedDates: true
      };
    }
    return {
      errorMessage: 'EventWidgets_HotelRequest_HotelNotAvailable__resx',
      hasRequestedOnlyShoulderDates: false,
      hasInvalidRequestedDates: true
    };
  }

  if (areDatesOnlyShoulderDatesRequiringApproval) {
    return {
      errorMessage: 'EventWidgets_HotelRequest_ifOnlyShoulderDateIsPicked__resx',
      hasRequestedOnlyShoulderDates: true,
      hasInvalidRequestedDates: false
    };
  }

  return;
}

function getPrimaryAttendeeDisplayName({ firstName, lastName }, translate) {
  return (!firstName && !lastName)
    ? translate('EventWidgets_HotelRequest_PrimaryNoName__resx')
    : formatAttendeeNameFromResource({ firstName, lastName }, translate);
}

/**
 * Gets the minimum and maximum price for room
 * @param {*} roomRate
 */
export function getPriceForHotelRoom(roomRate) {
  const rates = roomRate.filter(o => {
    return (o.rate === 0) ? null : o.rate;
  }).sort((one, two) => one.rate - two.rate);
  if (!rates.length) return null;
  return {
    minPrice: rates[0].rate,
    maxPrice: rates[rates.length - 1].rate
  };
}

/**
 * Gets the registrationTypeId for the selected occupant
 * @param {*} roomRate
 */
export function getRegistrationTypeIdForSelectedOccupant(registrations, attendeeRegistrationId) {
  if (registrations.primary && registrations.primary.eventRegistrationId === attendeeRegistrationId) {
    return registrations.primary.registrationTypeId;
  }
  if (registrations.guests && registrations.guests.find(r => r.eventRegistrationId === attendeeRegistrationId)) {
    return registrations.guests.find(r => r.eventRegistrationId === attendeeRegistrationId).registrationTypeId;
  }
}

/**
 * Filters the room type rates based on reg type and checkin checkout date
 * we can depecate this and use that or may be a new one going forward
 * @param {*} roomRates
 */
export function filterRoomDetailsByCheckinCheckoutDate(roomRates, checkinDate, checkoutDate,
  registrationTypeId) {
  const roomRatesByRegType = filterRoomRatesByRegType(roomRates, registrationTypeId);
  return roomRatesByRegType.filter((rate) => {
    const roomDate = moment(rate.roomRateDate);
    return roomDate.isSameOrAfter(checkinDate) && roomDate.isBefore(checkoutDate);
  });
}

/**
 * Duplicating this because the one above is not working correctly with UTC dates.
 * issue is if we skip time from room rate dates then the room rates returned are not for correct dates,
 * so we need to compare by converting both the dates to moment object
 */
export function filterRoomRatesForBookingByUTCDates(roomRates, checkinDateInUTC, checkoutDateInUTC,
  registrationTypeId) {
  const roomRatesByRegType = filterRoomRatesByRegType(roomRates, registrationTypeId);
  return roomRatesByRegType.filter((rate) => {
    const rateDate = moment(rate.roomRateDate);
    return rateDate.isSameOrAfter(moment(checkinDateInUTC)) && rateDate.isBefore(moment(checkoutDateInUTC));
  });
}

/**
 * returns recalculated SDA confirmed dates fields based on new rules (from hotel/advance rule)
 * NOTE: ** all dates should be in same timezone **
 * @param earliestArrival
 * @param latestDeparture
 * @param checkinDate
 * @param checkoutDate
 * @returns {{confirmedCheckinDate: Date, confirmedCheckoutDate: Date}}
 */
export function getNewConfirmedDates(earliestArrival, latestDeparture, checkinDate, checkoutDate) {
  let confirmedCheckinDate = moment(earliestArrival)
    .isAfter(checkinDate) ? earliestArrival : null;
  let confirmedCheckoutDate = moment(latestDeparture)
    .isBefore(checkoutDate) ? latestDeparture : null;
  return { confirmedCheckinDate, confirmedCheckoutDate };
}

/**
 * returns true if passed dates (Date part matches - ignoring time part here) matches
 * NOTE: ** all dates should be in same timezone **
 * @param date1
 * @param date2
 * @returns boolean
 */
export function areDatesEqual(date1, date2) {
  // if any one of date is null return false
  // if both are null return true
  return (!date1 && !date2) || (date1 && date2 && moment(date1).startOf('day').isSame(moment(date2).startOf('day')));
}

export const getHotelCheckinCheckoutValidationMessages = (translate, validationStatus) => {
  const validationMessage = {};
  if (validationStatus.invalidDateRange) {
    return {
      dateRange: translate('HotelRequest_invaliddaterange__resx')
    };
  }

  if (validationStatus.onlyShoulderDateRequested) {
    return {
      dateRange: translate('EventWidgets_HotelRequest_ifOnlyShoulderDateIsPicked__resx')
    };
  }

  if (validationStatus.invalidRequestedDates) {
    return {
      dateRange: translate('EventWidgets_HotelRequest_HotelNotAvailable__resx')
    };
  }

  let checkinValidationMessage = validationStatus.checkinDate === DATE_CHANGE_EMPTY_DATE
    ? translate('HotelRequest_emptycheckin__resx') : null;
  if (!checkinValidationMessage) {
    checkinValidationMessage = validationStatus.checkinDate === DATE_CHANGE_ERROR_INVALID
      ? translate('HotelRequest_invalidcheckin__resx') : null;
  }
  let checkoutValidationMessage = validationStatus.checkoutDate === DATE_CHANGE_EMPTY_DATE
    ? translate('HotelRequest_emptycheckout__resx') : null;
  if (!checkoutValidationMessage) {
    checkoutValidationMessage = validationStatus.checkoutDate === DATE_CHANGE_ERROR_INVALID
      ? translate('HotelRequest_invalidcheckout__resx') : null;
  }
  if (checkinValidationMessage) validationMessage.checkinDate = checkinValidationMessage;
  if (checkoutValidationMessage) validationMessage.checkoutDate = checkoutValidationMessage;
  return validationMessage;
};

/**
 * map 2 properties in roommateRequestSettings to roommateSetupSettings
 * allowRequestFullRoom -> inviteesCanRequestFullRoom
 * roommatePreferenceOption -> enteringRoommateOption
 * @param roommateRequestSettings
 * @returns
 */

export function mapToRoommateSetupSettings(roommateRequestSettings = {}) {
  return {
    ...roommateRequestSettings,
    inviteesCanRequestFullRoom: roommateRequestSettings.allowRequestFullRoom,
    enteringRoommateOption: roommateRequestSettings.roommatePreferenceOption
  };
}

/**
 * reverse mapping of roommateSetupSettings to roommateRequestSettings
 * inviteesCanRequestFullRoom -> allowRequestFullRoom
 * enteringRoommateOption -> roommatePreferenceOption
 * @param roommateSetupSettings
 * @returns
 */
export function mapFromRoommateSetupSettings(roommateSetupSettings) {
  if (!roommateSetupSettings) {
    return null;
  }

  return {
    ...roommateSetupSettings,
    allowRequestFullRoom: roommateSetupSettings.inviteesCanRequestFullRoom,
    roommatePreferenceOption: roommateSetupSettings.enteringRoommateOption
  };
}

/**
 * default roommate setup settings for appData based on experiment settings and isForGuestSide
 * @returns
 */
export function getRoommateRequestSettings(experimentSettings, isForGuestSide) {
  let roommateRequestSettings = {
    allowRequestFullRoom: true,
    allowRoommatePreference: true,
    roommatePreferenceOption: ROOMMATE_PREFERENCE_OPTION.VISIBLE
  };
  // add flex pre release settings
  if (experimentSettings.isFlexPreReleaseEnabled || isForGuestSide) {
    roommateRequestSettings = {
      ...roommateRequestSettings,
      roommatePreferenceType: ROOMMATE_PREFERENCE_TYPE.ENTER_NAME,
      availableRoommatesOption: AVAILABLE_ROOMMATES_OPTION.REGISTRANTS,
      allowRoommatesByRegType: false,
      regTypeMatchingOption: {
        type: REG_TYPE_MATCHING_OPTION.SAME_REG_TYPES,
        selectedValues: []
      },
      allowEnlistSelf: true,
      allowRoommateWithSameDatesOnly: true,
      roommateDisplayFields: []
    };
  }

  return roommateRequestSettings;
}
